import { ChangeDetectionStrategy, Component, ErrorHandler, EventEmitter, Input, OnInit, Output, signal } from '@angular/core'

import { RaporttiType } from '@lemon/core-firebase/model/reports-shared'
import {
  Kirjanpitotili,
  KirjanpitotilinPoistotiedot,
  RaporttiRequest,
  RaporttiTuloslaskelmaAccountRow,
  RaporttiTuloslaskelmaData,
  RaporttiTuloslaskelmaDataResponse,
  RaporttiTuloslaskelmaErikoisrivi,
} from 'app/_jaettu-lemonator/model/kirjanpito'
import { BehaviorSubject, Observable, combineLatest, of } from 'rxjs'
import { map, startWith, switchMap, tap } from 'rxjs/operators'
import { KlikattuKirjaus, RaporttienHakuvaihtoehdot } from '../raportit.component'

import { NumberDate, Timestamp } from '@lemon/core/model/common'
import { CurrencyService } from '@lemon/core/service/currency.service'
import { AsiakasService } from 'app/_angular/service/asiakas/asiakas.service'
import { FirebaseLemonator } from 'app/_angular/service/firebase-lemonator.service'
import { KirjanpitoNavigationService } from 'app/_angular/service/kirjanpito/kirjanpito-navigation.service'
import { TilikarttaService } from 'app/_angular/service/tilikartta.service'
import { TimestampService } from 'app/_jaettu-angular/service/timestamp-service'
import { AsiakkaanMaksutapa } from 'app/_jaettu-lemonator/model/asiakas'
import { KirjanpitoJaettuService } from 'app/_jaettu-lemonator/service/kirjanpito-jaettu.service'
import { TilikarttaJaettuService } from 'app/_jaettu-lemonator/service/tilikartta-jaettu.service'

interface RaporttiTuloslaskelmaAccountRowDisplay extends RaporttiTuloslaskelmaAccountRow {
  showR?: true
  poistotiedot?: KirjanpitotilinPoistotiedot
}

interface RaporttiTuloslaskelmaDataDisplay extends RaporttiTuloslaskelmaData {
  r: RaporttiTuloslaskelmaAccountRowDisplay[]
}

@Component({
  selector: '[app-kirjanpito-tuloslaskelma]',
  templateUrl: './tuloslaskelma.component.html',
  styleUrls: ['./tuloslaskelma.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class KirjanpitoRaportitTuloslaskelmaComponent implements OnInit {
  @Input() hakuvaihtoehdotObservable: Observable<RaporttienHakuvaihtoehdot>
  @Input() tilitMapObservable: Observable<Map<string, Kirjanpitotili>>
  @Input() paivitaArvotHiljaisestiSubject: BehaviorSubject<number>
  @Input() tulosEnsin: boolean

  @Output() kirjaustaKlikattiin: EventEmitter<KlikattuKirjaus> = new EventEmitter()
  @Output() taseEnsinClicked: EventEmitter<boolean> = new EventEmitter()

  readonly lataa = signal(true)
  tuloslaskelmanDataObservable: Observable<RaporttiTuloslaskelmaDataDisplay>

  readonly lastSucessfullyUpdated = signal<Timestamp>(undefined)

  private _expandedAccountsBehaviorSubject: BehaviorSubject<Set<string>> = new BehaviorSubject(new Set())

  constructor(
    private _errorHandler: ErrorHandler,
    private _firebase: FirebaseLemonator,
    private _asiakasService: AsiakasService,
    private _currencyService: CurrencyService,
    private _timestampService: TimestampService,
    private _tilikarttaJaettuService: TilikarttaJaettuService,
    private _tilikarttaService: TilikarttaService,
    private _kirjanpitoNavigationService: KirjanpitoNavigationService,
    private _kirjanpitoJaettuService: KirjanpitoJaettuService,
  ) {}

  ngOnInit() {
    const laskelmanRaakadataObservable = combineLatest([
      this._asiakasService.nykyinenAsiakasAvainObservable,
      this.hakuvaihtoehdotObservable.pipe(
        tap(() => {
          this.lataa.set(true)
        }),
      ),
      this.paivitaArvotHiljaisestiSubject,
    ]).pipe(
      switchMap(([asiakas, hakuvaihtoehdot, paivita]) => {
        if (!hakuvaihtoehdot?.alkaa || !hakuvaihtoehdot?.loppuu || !asiakas) {
          return of<RaporttiTuloslaskelmaData>(null)
        }

        const haku: RaporttiRequest = {
          a: asiakas.avain,
          k: 'fi',
          w: RaporttiType.TULOS,
          s: hakuvaihtoehdot.alkaa,
          e: hakuvaihtoehdot.loppuu,
          datasource: hakuvaihtoehdot.datasource,
        }
        // if (hakuvaihtoehdot.tilista) { haku.b = hakuvaihtoehdot.tilista }
        // if (hakuvaihtoehdot.tiliin) { haku.c = hakuvaihtoehdot.tiliin }
        // if (hakuvaihtoehdot.vapaasanahaku?.trim()) { haku.t = hakuvaihtoehdot.vapaasanahaku.trim() }
        if (hakuvaihtoehdot.projekti) {
          haku.p = hakuvaihtoehdot.projekti
        }

        if (this._expandedAccountsBehaviorSubject.value.size > 0) {
          haku.f = Array.from(this._expandedAccountsBehaviorSubject.value)
        }

        return this._firebase
          .functionsCall<RaporttiRequest, RaporttiTuloslaskelmaDataResponse>('kirjanpitoRaportitData', haku)
          .then(res => {
            if (res.e) {
              throw new Error(res.e)
            }
            this.lastSucessfullyUpdated.set(this._timestampService.now())
            this.lataa.set(false)
            return res.data
          })
          .catch(err => {
            console.error('Failed to fetch report data', err)
            this._errorHandler.handleError(err)
          })
      }),
      startWith<RaporttiTuloslaskelmaData>({
        c: 'c1',
        c1: null,
        r: [],
      }),
    )

    const maksutavatMapObservable: Observable<Map<string, AsiakkaanMaksutapa>> = this._asiakasService.nykyisenAsiakkaanKaikkiMaksutavatObservable.pipe(
      map(maksutavat => {
        const paymentMethodsMap = new Map<string, AsiakkaanMaksutapa>()
        for (const m of maksutavat) {
          paymentMethodsMap.set(m.tunniste + '', m)
        }
        return paymentMethodsMap
      }),
    )

    const dataJaNimet = combineLatest([this.tilitMapObservable, laskelmanRaakadataObservable as Observable<RaporttiTuloslaskelmaDataDisplay>, maksutavatMapObservable]).pipe(
      map(([tiliMap, laskelmanRaakadata, maksutavatMap]) => {
        if (!tiliMap || !laskelmanRaakadata || !maksutavatMap) {
          return null
        }
        if (laskelmanRaakadata.r?.length > 0) {
          for (const r of laskelmanRaakadata.r) {
            if (r.s) {
              r.n = 'Yhteensä'
            } else {
              if (r.a.length > 3) {
                r.n = r.a + ' ' + this._tilikarttaJaettuService.annaKirjanpitotilinNimi(tiliMap.get(r.a), 'fi')
              } else {
                r.n = this._tilikarttaJaettuService.annaKirjanpitotilinNimi(tiliMap.get(r.a), 'fi')
              }
            }
            if (r.a === RaporttiTuloslaskelmaErikoisrivi.LIIKEVOITTO) {
              r.n = 'LIIKEVOITTO'
            }
            if (r.a === RaporttiTuloslaskelmaErikoisrivi.TULOS_ENNEN_VEROJA) {
              r.n = 'TULOS ENNEN VEROJA'
            }
            if (r.d) {
              let debitSumma = 0
              let kreditSumma = 0
              for (const d of r.d) {
                d.ma = maksutavatMap.get(d.m)?.nimi || ''
                debitSumma += d.d ?? 0
                kreditSumma += d.k ?? 0
              }
              r.dks = this._currencyService.roundHalfUp(kreditSumma, 2)
              r.dds = this._currencyService.roundHalfUp(debitSumma, 2)
            }
            const poistotiedot = this._kirjanpitoJaettuService.annaRaporttiRivinTilinPoistotiedot(r, tiliMap)
            if (poistotiedot) {
              r.poistotiedot = poistotiedot
            } else {
              delete r.poistotiedot
            }
          }
          laskelmanRaakadata.r[laskelmanRaakadata.r.length - 1].n = 'TILIKAUDEN VOITTO'
        }
        return laskelmanRaakadata
      }),
    )

    const reskontraTilitSetObservable = this._tilikarttaService.nykyisenAsiakkaanTilikartanJaPaatilikartanTilitObservable.pipe(
      map(tilit => {
        return tilit.filter(t => t.reskontraActive)
      }),
      map(tilit => {
        const reskontraTilitSet = new Set<string>()
        for (const tili of tilit) {
          reskontraTilitSet.add(tili.numero)
        }
        return reskontraTilitSet
      }),
    )

    this.tuloslaskelmanDataObservable = combineLatest([dataJaNimet, this._expandedAccountsBehaviorSubject, reskontraTilitSetObservable]).pipe(
      map(([data, rivienExpand, reskontraTilitSet]) => {
        if (!data || !rivienExpand || !reskontraTilitSet) {
          return null
        }
        if (data?.r) {
          for (const r of data.r) {
            if (rivienExpand.has(r.a)) {
              r.e = 1
            } else {
              delete r.e
            }
            if (reskontraTilitSet.has(r.a)) {
              r.showR = true
            }
          }
        }
        return data
      }),
    )
  }

  handleClick(event: MouseEvent, data: RaporttiTuloslaskelmaData) {
    const element = event.target as HTMLElement
    if (element.dataset.n) {
      this.kirjaustaKlikattiin.emit({ kirjausnumero: element.dataset.n })
    } else if (element?.classList?.contains('n')) {
      const tilinumero = element.parentElement.dataset.tnro
      if (tilinumero?.length > 3) {
        const set = this._expandedAccountsBehaviorSubject.value
        if (set.has(tilinumero)) {
          set.delete(tilinumero)
        } else {
          set.add(tilinumero)
          this.paivitaArvotHiljaisestiSubject.next(this.paivitaArvotHiljaisestiSubject.value + 1)
        }
        this._expandedAccountsBehaviorSubject.next(set)
        event.preventDefault()
        event.stopPropagation()
      }
    } else if (data?.r) {
      const tr = this._findTr(element)
      if (tr?.dataset.i !== undefined) {
        const closestBody = tr.parentElement as HTMLTableSectionElement
        if (closestBody?.dataset?.n) {
          // console.log('secondTd found', closestBody)
          const tilinumero = closestBody.dataset.n
          const tilinItem = data.r.find(a => a.n === tilinumero)
          if (tilinItem?.d) {
            // console.log('item found', tilinItem)
            const d = tilinItem.d[Number(tr?.dataset.i)]
            if (d.l) {
              delete d.l
            } else {
              d.l = true
            }
          }
        }
      }
    }
  }

  private _findTr(elem: HTMLElement): HTMLTableRowElement {
    if (elem?.tagName === 'TR') {
      return elem as HTMLTableRowElement
    } else if (elem?.parentElement?.tagName === 'TR') {
      return elem.parentElement as HTMLTableRowElement
    } else if (elem?.parentElement?.parentElement?.tagName === 'TR') {
      return elem.parentElement.parentElement as HTMLTableRowElement
    }
    return null
  }

  trackAccountRowByAccountNumberFn(index: number, item: RaporttiTuloslaskelmaAccountRow) {
    return item.a
  }

  avaaReskontraValilehti(tili: string, raportinPaattymispaiva: NumberDate) {
    this._kirjanpitoNavigationService.vaihdaPaaTasonValilehti('reskontra')
    this._kirjanpitoNavigationService.valittuKirjanpidonTili(tili)
    this._kirjanpitoNavigationService.valittuRaporttiValinPaattymispaiva(raportinPaattymispaiva)
  }
}
